import { useEffect } from 'react';

import { usePathname } from 'expo-router';

import { useExperiments } from 'state/experiments';
import { selectors, useAppSelector } from 'state/global-state';
import { logger } from 'utils/logger';

import { useMinimalCartUi } from './use-minimal-cart-ui';

export type CartRecoveryMode = 'legacy' | 'minimal';

type ResolveCartRecoveryModeArgs = {
  isMinimalCartUiEnabled: boolean;
  legacyCartEntriesCount: number;
  minimalCartEntriesCount: number;
};

export const resolveCartRecoveryMode = ({
  isMinimalCartUiEnabled,
  legacyCartEntriesCount,
  minimalCartEntriesCount,
}: ResolveCartRecoveryModeArgs): CartRecoveryMode => {
  if (minimalCartEntriesCount > 0 && legacyCartEntriesCount === 0) {
    return 'minimal';
  }

  if (legacyCartEntriesCount > 0 && minimalCartEntriesCount === 0) {
    return 'legacy';
  }

  return isMinimalCartUiEnabled ? 'minimal' : 'legacy';
};

export const useCartRecoveryMode = ({ logRecoveryOverride = false } = {}) => {
  const isMinimalCartUiEnabled = useMinimalCartUi();
  // While experiments are still loading, `useMinimalCartUi()` reads `false` regardless of the
  // user's real assignment, so `mode` can transiently resolve to `legacy` for a minimal-cart user
  // on a hard reload. Callers must not run empty-cart redirect logic until this settles, otherwise
  // the empty legacy cart redirects a minimal-cart user to the menu before their cart hydrates.
  const { isLoading: isResolving } = useExperiments();
  const legacyCartEntries = useAppSelector(selectors.ordering.selectCartEntries);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const pathname = usePathname();
  const legacyCartEntriesCount = legacyCartEntries.length;
  const minimalCartEntriesCount = minimalCartEntries.length;
  const mode = resolveCartRecoveryMode({
    isMinimalCartUiEnabled,
    legacyCartEntriesCount,
    minimalCartEntriesCount,
  });
  const experimentSelectedMode: CartRecoveryMode = isMinimalCartUiEnabled ? 'minimal' : 'legacy';
  const didOverrideExperimentMode = mode !== experimentSelectedMode;

  useEffect(() => {
    if (!logRecoveryOverride || !didOverrideExperimentMode) {
      return;
    }

    logger.info({
      message: 'Cart recovery mode override',
      cartRecoveryMode: mode,
      experimentSelectedMode,
      legacyCartEntriesCount,
      minimalCartEntriesCount,
      pathname,
    });
  }, [
    didOverrideExperimentMode,
    experimentSelectedMode,
    legacyCartEntriesCount,
    logRecoveryOverride,
    minimalCartEntriesCount,
    mode,
    pathname,
  ]);

  return {
    mode,
    isResolving,
    isMinimalCartUiEnabled,
    didOverrideExperimentMode,
    legacyCartEntriesCount,
    minimalCartEntriesCount,
  } as const;
};
